import { fetchWithRefresh } from "../../api/FetchWithRefresh";
import { AnchorsText } from "./AnchorsText";
import type { RelevantTopicParsedRow } from "./AnchorsText";
import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import type { paths } from "../../api/generated/api-types";
import { buildCloneFormState, buildFullFormatAnchorsText, type CloneFrom } from "./BuildCloneFormState";
import { PUBLICATION_FILTER_DEFAULTS } from "../shared/publicationFilterDefaults";

// Единая форма тела ошибки бэкенда: обработчики читают из него текст сообщения.
interface ErrorBody {
  // Отсутствует, когда бэкенд ответил ошибкой без разобранного тела: сообщение
  // тогда собирается из кода ответа.
  error?: string;
  // Отсутствует, когда отказ не связан с полями формы: подсвечивать нечего.
  fields?: unknown;
}

type ListSiteRegionsResponse =
  paths["/sites/regions"]["get"]["responses"]["200"]["content"]["application/json"];

type ListGeneralTopicsResponse =
  paths["/general-topics"]["get"]["responses"]["200"]["content"]["application/json"];

type CreateAnchorTopicRequest =
  paths["/tasks/anchor-topic"]["post"]["requestBody"]["content"]["application/json"];
type CreateRelevantTopicRequest =
  paths["/tasks/relevant-topic"]["post"]["requestBody"]["content"]["application/json"];
type CreateFixedRelevantTopicRequest =
  paths["/tasks/fixed-relevant-topic"]["post"]["requestBody"]["content"]["application/json"];
type CreateHomepagePublicationRequest =
  paths["/tasks/homepage-publication"]["post"]["requestBody"]["content"]["application/json"];
type CreateSitewidePublicationRequest =
  paths["/tasks/sitewide-publication"]["post"]["requestBody"]["content"]["application/json"];

type CreateTasksResponse =
  paths["/tasks/anchor-topic"]["post"]["responses"]["201"]["content"]["application/json"];

export type TaskType =
  | "generation_anchor_topic"
  | "generation_relevant_topic"
  | "generation_relevant_topic_fixed"
  | "homepage_publication"
  | "sitewide_publication";

export type AnchorEntry = CreateAnchorTopicRequest["tasks"][number]["anchors"][number];
export type RelevantAnchorEntry = CreateRelevantTopicRequest["tasks"][number]["anchors"][number];
export type StructureParams = CreateAnchorTopicRequest["structureParams"];
export type GeneralTopicItem = ListGeneralTopicsResponse["items"][number];
// Filter-selection parameters for the auto-started publication (screen doc «Параметры
// публикации»); identical shape across all five task-creation requests.
export type PublicationParams = NonNullable<CreateAnchorTopicRequest["publicationParams"]>;

export type RelevantTopicAnchorRow = {
  text: string;
  articleCount: number;
  uniqueness: number;
  generalTopic: string;
};

export type AnchorInputMode =
  | { mode: "textarea" }
  | { mode: "table"; rows: RelevantTopicAnchorRow[] }
  | { mode: "fixed-topics"; topics: string[] };

export type UrlGroup = {
  targetUrl: string;
  anchorsText: string;
  comment: string;
  uniqueArticlesCount: number;
  anchorInputMode: AnchorInputMode;
};

export type SharedFormFields = {
  taskType: TaskType;
  language: string;
  region: string;
  autoPublish: boolean;
  structureParams: StructureParams;
  publicationParams: PublicationParams;
};

export type FieldErrors = Partial<Record<string, string>>;
export type GroupErrors = Record<number, FieldErrors>;

type ScreenState =
  | { kind: "form" }
  | { kind: "submitting" }
  | { kind: "error"; fieldErrors: FieldErrors; groupErrors: GroupErrors }
  | { kind: "success" };

export type RegionsState =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "loaded"; regions: { region: string; geoGroup: string; siteCount: number }[] };

export type TopicsState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "loaded"; topics: GeneralTopicItem[] }
  | { kind: "error"; message: string };

export interface UseTaskCreationResult {
  shared: SharedFormFields;
  groups: UrlGroup[];
  screenState: ScreenState;
  regionsState: RegionsState;
  topicsState: TopicsState;
  setSharedField: <K extends keyof SharedFormFields>(key: K, value: SharedFormFields[K]) => void;
  setStructureParameter: <K extends keyof StructureParams>(key: K, value: StructureParams[K]) => void;
  setPublicationParameter: <K extends keyof PublicationParams>(key: K, value: PublicationParams[K]) => void;
  addGroup: () => void;
  removeGroup: (index: number) => void;
  setGroupField: (
    index: number,
    key: "targetUrl" | "anchorsText" | "comment",
    value: string,
  ) => void;
  setGroupAnchorRow: (
    groupIndex: number,
    anchorIndex: number,
    field: keyof RelevantTopicAnchorRow,
    value: string | number,
  ) => void;
  setGroupUniqueArticlesCount: (index: number, value: number) => void;
  setGroupTopicRow: (groupIndex: number, topicIndex: number, value: string) => void;
  processGroupAnchors: (index: number) => void;
  resetGroupAnchorsToTextarea: (index: number) => void;
  submit: () => void;
  retryRegions: () => void;
  retryTopics: () => void;
}

export const INITIAL_STRUCTURE_PARAMETERS: StructureParams = {
  listCount: 0,
  tableCount: 0,
  faqEnabled: false,
  faqQuestionCount: 0,
};

const EMPTY_GROUP: UrlGroup = {
  targetUrl: "",
  anchorsText: "",
  comment: "",
  uniqueArticlesCount: 1,
  anchorInputMode: { mode: "textarea" },
};

const INITIAL_SHARED: SharedFormFields = {
  taskType: "generation_anchor_topic",
  language: "English",
  region: "",
  autoPublish: true,
  structureParams: INITIAL_STRUCTURE_PARAMETERS,
  publicationParams: PUBLICATION_FILTER_DEFAULTS,
};

const FIELD_ERROR_MESSAGES: Record<string, string> = {
  language: "Выберите язык",
  region: "Выберите регион",
  tasks: "Добавьте хотя бы одну URL-группу",
  "structureParams.listCount": "Укажите количество списков (число ≥ 0)",
  "structureParams.tableCount": "Укажите количество таблиц (число ≥ 0)",
  "structureParams.faqEnabled": "Некорректное значение FAQ",
  "structureParams.faqQuestionCount": "Укажите количество вопросов FAQ (число ≥ 0)",
};

const GROUP_FIELD_ERROR_MESSAGES: Record<string, string> = {
  targetUrl: "Укажите целевой URL (например, https://example.com)",
  anchors: "Добавьте хотя бы один анкор в формате: anchor,количество",
  uniqueArticlesCount: "Количество уникальных статей: целое ≥ 1 и ≤ суммы публикаций",
  topics: "Заполните темы (по одной на уникальную статью)",
};

const ENDPOINT_BY_TASK_TYPE: Record<TaskType, string> = {
  generation_anchor_topic: "/api/tasks/anchor-topic",
  generation_relevant_topic: "/api/tasks/relevant-topic",
  generation_relevant_topic_fixed: "/api/tasks/fixed-relevant-topic",
  homepage_publication: "/api/tasks/homepage-publication",
  sitewide_publication: "/api/tasks/sitewide-publication",
};

/**
 * Состояние навигации, с которым экран открывают из карточки существующей
 * задачи, чтобы создать её копию.
 */
interface CloneNavigationState {
  // Отсутствует, когда на экран пришли обычным переходом «создать задачу»:
  // клонировать тогда нечего.
  cloneFrom?: CloneFrom;
}

export function useTaskCreation(): UseTaskCreationResult {
  const location = useLocation();
  const locationState = location.state as CloneNavigationState | null;
  const cloneFrom: CloneFrom | null =
    locationState && locationState.cloneFrom ? locationState.cloneFrom : null;

  const [shared, setShared] = useState<SharedFormFields>(() =>
    cloneFrom ? buildCloneFormState(cloneFrom, () => "").shared : INITIAL_SHARED,
  );
  const [groups, setGroups] = useState<UrlGroup[]>(() =>
    cloneFrom
      ? buildCloneFormState(cloneFrom, () => "").groups
      : [{ ...EMPTY_GROUP, anchorInputMode: { mode: "textarea" } }],
  );
  const [screenState, setScreenState] = useState<ScreenState>({ kind: "form" });
  const [regionsState, setRegionsState] = useState<RegionsState>({ kind: "loading" });
  const [topicsState, setTopicsState] = useState<TopicsState>({ kind: "idle" });
  const navigate = useNavigate();

  const loadRegions = useCallback(() => {
    const nonHomepageCredentialType = shared.taskType === "sitewide_publication" ? "sitewide" : "article";
    const credentialType =
      shared.taskType === "homepage_publication" ? "homepage" : nonHomepageCredentialType;
    setRegionsState({ kind: "loading" });
    fetchWithRefresh(`/api/sites/regions?credentialType=${credentialType}`)
      .then(async (response) => {
        if (response.ok) {
          const data = (await response.json()) as ListSiteRegionsResponse;
          const selectableRegions = data.regions.filter((row) => row.geoGroup !== "secondary");
          setRegionsState({ kind: "loaded", regions: selectableRegions });
        } else {
          setRegionsState({ kind: "error", message: "Ошибка загрузки регионов" });
        }
      })
      .catch(() => {
        setRegionsState({ kind: "error", message: "Ошибка сети" });
      });
  }, [shared.taskType]);

  const loadTopics = useCallback((): Promise<GeneralTopicItem[] | null> => {
    setTopicsState({ kind: "loading" });
    return fetchWithRefresh("/api/general-topics")
      .then(async (response) => {
        let loadedTopics: GeneralTopicItem[] | null = null;
        if (response.ok) {
          const data = (await response.json()) as ListGeneralTopicsResponse;
          setTopicsState({ kind: "loaded", topics: data.items });
          loadedTopics = data.items;
        } else {
          setTopicsState({ kind: "error", message: "Ошибка загрузки тем" });
        }
        return loadedTopics;
      })
      .catch(() => {
        setTopicsState({ kind: "error", message: "Ошибка сети" });
        return null;
      });
  }, []);

  useEffect(() => {
    loadRegions();
  }, [loadRegions]);

  // Clone mode: load the topics dictionary upfront for relevant/fixed types so the
  // already-processed group renders with populated dropdowns, then resolve any null
  // snapshot topics to a random processing default (screen doc «Клонирование»).
  const cloneTopicsInitDone = useRef(false);
  useEffect(() => {
    if (cloneFrom === null || cloneTopicsInitDone.current) {
      return;
    }
    const needsTopics =
      shared.taskType === "generation_relevant_topic" ||
      shared.taskType === "generation_relevant_topic_fixed";
    if (!needsTopics) {
      return;
    }
    cloneTopicsInitDone.current = true;
    void loadTopics().then((topics) => {
      if (topics) {
        setGroups(patchPlaceholderTopics(makeDefaultTopicPicker(topics)));
      }
    });
  }, [cloneFrom, shared.taskType, loadTopics]);

  // Clone mode: once regions finish loading, if the original region is no longer among
  // the selectable options (no suitable sites remain) reset it to an empty select so the
  // user must pick again; existing submit validation blocks submission until they do.
  const cloneRegionCheckDone = useRef(false);
  useEffect(() => {
    if (cloneFrom === null || cloneRegionCheckDone.current) {
      return;
    }
    if (regionsState.kind !== "loaded") {
      return;
    }
    cloneRegionCheckDone.current = true;
    const regionAvailable = regionsState.regions.some((row) => row.region === shared.region);
    if (!regionAvailable) {
      setShared((previous) => ({ ...previous, region: "" }));
    }
  }, [cloneFrom, regionsState, shared.region]);

  const retryRegions = useCallback(() => {
    loadRegions();
  }, [loadRegions]);

  const retryTopics = useCallback(() => {
    void loadTopics().then((topics) => {
      if (topics && cloneFrom !== null) {
        setGroups(patchPlaceholderTopics(makeDefaultTopicPicker(topics)));
      }
    });
  }, [loadTopics, cloneFrom]);

  const setSharedField = useCallback(
    <K extends keyof SharedFormFields>(key: K, value: SharedFormFields[K]) => {
      setShared((previous) => {
        let next: SharedFormFields = { ...previous, [key]: value };
        if (key === "autoPublish" && value) {
          // Screen doc «Переключение автозапуска публикации»: turning autoPublish back on
          // reopens «Параметры публикации» with its initial values.
          next = { ...next, publicationParams: PUBLICATION_FILTER_DEFAULTS };
        }
        return next;
      });
      if (key === "taskType") {
        // Screen doc «Выбор типа задачи»: все URL-группы сбрасываются — textarea анкоров
        // очищается, а обработанная таблица/список тем сворачивается обратно в textarea.
        setGroups((previous) =>
          previous.map((group) => ({
            ...group,
            anchorsText: "",
            anchorInputMode: { mode: "textarea" },
          })),
        );
        setTopicsState({ kind: "idle" });
      }
    },
    [],
  );

  const setStructureParameter = useCallback(
    <K extends keyof StructureParams>(key: K, value: StructureParams[K]) => {
      setShared((previous) => ({
        ...previous,
        structureParams: { ...previous.structureParams, [key]: value },
      }));
    },
    [],
  );

  const setPublicationParameter = useCallback(
    <K extends keyof PublicationParams>(key: K, value: PublicationParams[K]) => {
      setShared((previous) => {
        let publicationParameters: PublicationParams = { ...previous.publicationParams, [key]: value };
        if (key === "regions" && value === null) {
          // Screen doc TaskCreation «Параметры публикации»: secondary sites are always
          // included while all regions is selected, so the checkbox renders checked —
          // switching all regions back on must restore that in state, not only in render.
          publicationParameters = { ...publicationParameters, includeSecondary: true };
        }
        return { ...previous, publicationParams: publicationParameters };
      });
    },
    [],
  );

  const addGroup = useCallback(() => {
    setGroups((previous) => [
      ...previous,
      { ...EMPTY_GROUP, anchorInputMode: { mode: "textarea" } },
    ]);
  }, []);

  const removeGroup = useCallback((index: number) => {
    setGroups((previous) => {
      let next = previous;
      if (previous.length > 1) {
        next = previous.filter((_, i) => i !== index);
      }
      return next;
    });
  }, []);

  const setGroupField = useCallback(
    (index: number, key: "targetUrl" | "anchorsText" | "comment", value: string) => {
      setGroups((previous) =>
        previous.map((group, i) => (i === index ? { ...group, [key]: value } : group)),
      );
    },
    [],
  );

  const setGroupAnchorRow = useCallback(
    (
      groupIndex: number,
      anchorIndex: number,
      field: keyof RelevantTopicAnchorRow,
      value: string | number,
    ) => {
      setGroups((previous) =>
        previous.map((group, i) => {
          let nextGroup = group;
          if (i === groupIndex && group.anchorInputMode.mode === "table") {
            const newRows = group.anchorInputMode.rows.map((row, j) =>
              j === anchorIndex ? { ...row, [field]: value } : row,
            );
            nextGroup = { ...group, anchorInputMode: { mode: "table", rows: newRows } };
          }
          return nextGroup;
        }),
      );
    },
    [],
  );

  const setGroupUniqueArticlesCount = useCallback((index: number, value: number) => {
    setGroups((previous) =>
      previous.map((group, i) => (i === index ? { ...group, uniqueArticlesCount: value } : group)),
    );
  }, []);

  const setGroupTopicRow = useCallback((groupIndex: number, topicIndex: number, value: string) => {
    setGroups((previous) =>
      previous.map((group, i) => {
        let nextGroup = group;
        if (i === groupIndex && group.anchorInputMode.mode === "fixed-topics") {
          const newTopics = group.anchorInputMode.topics.map((topic, j) =>
            j === topicIndex ? value : topic,
          );
          nextGroup = { ...group, anchorInputMode: { mode: "fixed-topics", topics: newTopics } };
        }
        return nextGroup;
      }),
    );
  }, []);

  const resetGroupAnchorsToTextarea = useCallback((index: number) => {
    setGroups((previous) =>
      previous.map((group, i) =>
        i === index ? { ...group, anchorInputMode: { mode: "textarea" } } : group,
      ),
    );
    setScreenState({ kind: "form" });
  }, []);

  const processGroupAnchors = useCallback(
    (index: number) => {
      const group = groups[index];

      if (shared.taskType === "generation_relevant_topic_fixed") {
        const fixedParse = AnchorsText.parse(group.anchorsText);
        if (fixedParse.error !== null) {
          setScreenState({ kind: "error", fieldErrors: {}, groupErrors: { [index]: { anchors: fixedParse.error } } });
          return;
        }
        if (fixedParse.anchors.length === 0) {
          setScreenState({ kind: "error", fieldErrors: {}, groupErrors: { [index]: { anchors: "Добавьте хотя бы один анкор" } } });
          return;
        }
        const totalArticleCount = fixedParse.anchors.reduce((sum, anchor) => sum + anchor.articleCount, 0);
        if (!Number.isInteger(group.uniqueArticlesCount) || group.uniqueArticlesCount < 1) {
          setScreenState({ kind: "error", fieldErrors: {}, groupErrors: { [index]: { uniqueArticlesCount: "Укажите количество уникальных статей (≥ 1)" } } });
          return;
        }
        if (group.uniqueArticlesCount > totalArticleCount) {
          setScreenState({ kind: "error", fieldErrors: {}, groupErrors: { [index]: { uniqueArticlesCount: "Не больше суммарного количества публикаций по анкорам" } } });
          return;
        }
        if (topicsState.kind === "loaded") {
          applyTopicsToFixedGroup({ setGroups, groupIndex: index, topics: topicsState.topics }, group.uniqueArticlesCount);
          setScreenState({ kind: "form" });
          return;
        }
        void loadTopics().then((topics) => {
          if (topics) {
            applyTopicsToFixedGroup({ setGroups, groupIndex: index, topics }, group.uniqueArticlesCount);
            setScreenState({ kind: "form" });
          }
        });
        return;
      }

      const parseResult = AnchorsText.parseRelevantTopic(group.anchorsText);
      if (parseResult.error) {
        setScreenState({
          kind: "error",
          fieldErrors: {},
          groupErrors: { [index]: { anchors: parseResult.error } },
        });
        return;
      }
      if (parseResult.rows.length === 0) {
        setScreenState({
          kind: "error",
          fieldErrors: {},
          groupErrors: { [index]: { anchors: "Добавьте хотя бы один анкор" } },
        });
        return;
      }

      if (topicsState.kind === "loaded") {
        applyTopicsToGroup({ setGroups, groupIndex: index, topics: topicsState.topics }, parseResult.rows);
        setScreenState({ kind: "form" });
        return;
      }

      void loadTopics().then((topics) => {
        if (topics) {
          applyTopicsToGroup({ setGroups, groupIndex: index, topics }, parseResult.rows);
          setScreenState({ kind: "form" });
        }
      });
    },
    [groups, topicsState, shared.taskType, loadTopics],
  );

  const submit = useCallback(() => {
    const taskType = shared.taskType;

    if (taskType === "generation_anchor_topic") {
      const submission = collectAnchorTopicGroups(groups);
      if (submission.errors) {
        setScreenState({ kind: "error", fieldErrors: {}, groupErrors: submission.errors });
        return;
      }
      const body: CreateAnchorTopicRequest = {
        language: shared.language as CreateAnchorTopicRequest["language"],
        region: shared.region,
        structureParams: shared.structureParams,
        autoPublish: shared.autoPublish,
        ...publicationParamsBody(shared),
        tasks: submission.tasks,
      };
      sendCreateRequest(ENDPOINT_BY_TASK_TYPE[taskType], body, setScreenState, navigate);
      return;
    }

    if (taskType === "homepage_publication") {
      const submission = collectAnchorTopicGroups(groups);
      if (submission.errors) {
        setScreenState({ kind: "error", fieldErrors: {}, groupErrors: submission.errors });
        return;
      }
      const body: CreateHomepagePublicationRequest = {
        language: shared.language as CreateHomepagePublicationRequest["language"],
        region: shared.region,
        autoPublish: shared.autoPublish,
        ...publicationParamsBody(shared),
        tasks: submission.tasks,
      };
      sendCreateRequest(ENDPOINT_BY_TASK_TYPE[taskType], body, setScreenState, navigate);
      return;
    }

    if (taskType === "sitewide_publication") {
      const submission = collectAnchorTopicGroups(groups);
      if (submission.errors) {
        setScreenState({ kind: "error", fieldErrors: {}, groupErrors: submission.errors });
        return;
      }
      const body: CreateSitewidePublicationRequest = {
        language: shared.language as CreateSitewidePublicationRequest["language"],
        region: shared.region,
        autoPublish: shared.autoPublish,
        ...publicationParamsBody(shared),
        tasks: submission.tasks,
      };
      sendCreateRequest(ENDPOINT_BY_TASK_TYPE[taskType], body, setScreenState, navigate);
      return;
    }

    if (taskType === "generation_relevant_topic_fixed") {
      const fixedSubmission = collectFixedRelevantTopicGroups(groups);
      if (fixedSubmission.errors) {
        setScreenState({ kind: "error", fieldErrors: {}, groupErrors: fixedSubmission.errors });
        return;
      }
      const fixedBody: CreateFixedRelevantTopicRequest = {
        language: shared.language as CreateFixedRelevantTopicRequest["language"],
        region: shared.region,
        structureParams: shared.structureParams,
        autoPublish: shared.autoPublish,
        ...publicationParamsBody(shared),
        tasks: fixedSubmission.tasks,
      };
      sendCreateRequest(ENDPOINT_BY_TASK_TYPE[taskType], fixedBody, setScreenState, navigate);
      return;
    }

    const submission = collectRelevantTopicGroups(groups);
    if (submission.errors) {
      setScreenState({ kind: "error", fieldErrors: {}, groupErrors: submission.errors });
      return;
    }
    const body: CreateRelevantTopicRequest = {
      language: shared.language as CreateRelevantTopicRequest["language"],
      region: shared.region,
      structureParams: shared.structureParams,
      autoPublish: shared.autoPublish,
      ...publicationParamsBody(shared),
      tasks: submission.tasks,
    };
    sendCreateRequest(ENDPOINT_BY_TASK_TYPE[taskType], body, setScreenState, navigate);
  }, [shared, groups, navigate]);

  return {
    shared,
    groups,
    screenState,
    regionsState,
    topicsState,
    setSharedField,
    setStructureParameter,
    setPublicationParameter,
    addGroup,
    removeGroup,
    setGroupField,
    setGroupAnchorRow,
    setGroupUniqueArticlesCount,
    setGroupTopicRow,
    processGroupAnchors,
    resetGroupAnchorsToTextarea,
    submit,
    retryRegions,
    retryTopics,
  };
}

/**
 * Clone-mode topics resolution: patch existing groups in place instead of rebuilding
 * from the snapshot, so any edits the user made while the topics dictionary was still
 * loading (or errored) are preserved. Only empty-string placeholder topics — the value
 * the pure mapper writes for a null snapshot topic before the dictionary is available —
 * are replaced with a processing default; every other field is left untouched.
 */
function patchPlaceholderTopics(
  defaultTopicPicker: () => string,
): (previous: UrlGroup[]) => UrlGroup[] {
  return (previous) =>
    previous.map((group) => {
      let patchedGroup = group;
      if (group.anchorInputMode.mode === "table") {
        const rows = group.anchorInputMode.rows.map((row) =>
          row.generalTopic === "" ? { ...row, generalTopic: defaultTopicPicker() } : row,
        );
        // Regenerate the full-format mirror from the patched rows so «Вернуться к textarea»
        // round-trips (a stale mirror would keep an invalid trailing-empty-topic line).
        patchedGroup = {
          ...group,
          anchorsText: buildFullFormatAnchorsText(rows),
          anchorInputMode: { mode: "table", rows },
        };
      }
      if (group.anchorInputMode.mode === "fixed-topics") {
        const topics = group.anchorInputMode.topics.map((topic) =>
          topic === "" ? defaultTopicPicker() : topic,
        );
        patchedGroup = { ...group, anchorInputMode: { mode: "fixed-topics", topics } };
      }
      return patchedGroup;
    });
}

/**
 * Conditional body fragment for the shared field `publicationParams` (screen doc
 * «Параметры публикации»): included only while autoPublish is on — a disabled
 * autoPublish never sends the block, regardless of its edited values.
 */
interface PublicationParamsBody {
  // Отсутствует, когда autoPublish выключен: поле не отправляется вовсе,
  // публикация запускается только вручную из TaskDetail.
  publicationParams?: PublicationParams;
}

function publicationParamsBody(shared: SharedFormFields): PublicationParamsBody {
  let body: PublicationParamsBody = {};
  if (shared.autoPublish) {
    body = { publicationParams: shared.publicationParams };
  }
  return body;
}

function makeDefaultTopicPicker(topics: GeneralTopicItem[]): () => string {
  const defaultTopics = topics.filter((topic) => topic.isDefault);
  return () => {
    let pickedTopic = "";
    if (defaultTopics.length > 0) {
      pickedTopic = defaultTopics[Math.floor(Math.random() * defaultTopics.length)].topic;
    }
    return pickedTopic;
  };
}

/**
 * Адрес расстановки тем: в какую группу списка пишем, каким списком групп
 * управляем и по какому словарю тем подбираем значения по умолчанию. Три
 * значения ходят вместе через все операции расстановки, поэтому собраны в одно
 * понятие, а не разложены по сигнатурам.
 */
interface GroupTopicsTarget {
  setGroups: React.Dispatch<React.SetStateAction<UrlGroup[]>>;
  groupIndex: number;
  topics: GeneralTopicItem[];
}

function applyTopicsToFixedGroup(target: GroupTopicsTarget, uniqueArticlesCount: number): void {
  const { setGroups, groupIndex, topics } = target;
  const pickDefault = makeDefaultTopicPicker(topics);
  const rows: string[] = Array.from({ length: uniqueArticlesCount }, () => pickDefault());
  setGroups((previous) =>
    previous.map((group, i) =>
      i === groupIndex ? { ...group, anchorInputMode: { mode: "fixed-topics", topics: rows } } : group,
    ),
  );
}

type FixedRelevantTopicCollectSuccess = { tasks: CreateFixedRelevantTopicRequest["tasks"]; errors: null };
type FixedRelevantTopicCollectFailure = { tasks: null; errors: GroupErrors };

function collectFixedRelevantTopicGroups(
  groups: UrlGroup[],
): FixedRelevantTopicCollectSuccess | FixedRelevantTopicCollectFailure {
  let fixedRelevantTopicGroups: FixedRelevantTopicCollectSuccess | FixedRelevantTopicCollectFailure;
  const errors: GroupErrors = {};
  const tasks: CreateFixedRelevantTopicRequest["tasks"] = [];
  let hasError = false;
  for (let i = 0; i < groups.length; i++) {
    const group = groups[i];
    if (group.anchorInputMode.mode !== "fixed-topics") {
      errors[i] = { anchors: "Нажмите «Обработать» для подбора тем" };
      hasError = true;
      continue;
    }
    const parsed = AnchorsText.parse(group.anchorsText);
    if (parsed.error !== null) {
      errors[i] = { anchors: parsed.error };
      hasError = true;
      continue;
    }
    const topics = group.anchorInputMode.topics;
    if (topics.some((topic) => topic.trim() === "")) {
      errors[i] = { topics: "Заполните все темы" };
      hasError = true;
      continue;
    }
    tasks.push({
      targetUrl: group.targetUrl,
      uniqueArticlesCount: group.uniqueArticlesCount,
      anchors: parsed.anchors,
      topics,
      ...(group.comment ? { comment: group.comment } : {}),
    });
  }
  if (hasError) {
    fixedRelevantTopicGroups = { tasks: null, errors };
  } else {
    fixedRelevantTopicGroups = { tasks, errors: null };
  }
  return fixedRelevantTopicGroups;
}

function applyTopicsToGroup(target: GroupTopicsTarget, parsedRows: RelevantTopicParsedRow[]): void {
  const { setGroups, groupIndex, topics } = target;
  const getDefaultTopic = makeDefaultTopicPicker(topics);
  const rows: RelevantTopicAnchorRow[] = parsedRows.map((row) => ({
    text: row.text,
    articleCount: row.articleCount,
    uniqueness: row.uniqueness ?? 5,
    generalTopic: row.generalTopic ?? getDefaultTopic(),
  }));
  setGroups((previous) =>
    previous.map((group, i) =>
      i === groupIndex ? { ...group, anchorInputMode: { mode: "table", rows } } : group,
    ),
  );
}

type AnchorTopicCollectSuccess = { tasks: CreateAnchorTopicRequest["tasks"]; errors: null };
type AnchorTopicCollectFailure = { tasks: null; errors: GroupErrors };

function collectAnchorTopicGroups(groups: UrlGroup[]): AnchorTopicCollectSuccess | AnchorTopicCollectFailure {
  let anchorTopicGroups: AnchorTopicCollectSuccess | AnchorTopicCollectFailure;
  const errors: GroupErrors = {};
  const tasks: CreateAnchorTopicRequest["tasks"] = [];
  let hasError = false;
  for (let i = 0; i < groups.length; i++) {
    const group = groups[i];
    const parsed = AnchorsText.parse(group.anchorsText);
    if (parsed.error) {
      errors[i] = { anchors: parsed.error };
      hasError = true;
      continue;
    }
    tasks.push({
      targetUrl: group.targetUrl,
      anchors: parsed.anchors,
      ...(group.comment ? { comment: group.comment } : {}),
    });
  }
  if (hasError) {
    anchorTopicGroups = { tasks: null, errors };
  } else {
    anchorTopicGroups = { tasks, errors: null };
  }
  return anchorTopicGroups;
}

type RelevantTopicCollectSuccess = { tasks: CreateRelevantTopicRequest["tasks"]; errors: null };
type RelevantTopicCollectFailure = { tasks: null; errors: GroupErrors };

function collectRelevantTopicGroups(
  groups: UrlGroup[],
): RelevantTopicCollectSuccess | RelevantTopicCollectFailure {
  let relevantTopicGroups: RelevantTopicCollectSuccess | RelevantTopicCollectFailure;
  const errors: GroupErrors = {};
  const tasks: CreateRelevantTopicRequest["tasks"] = [];
  let hasError = false;
  for (let i = 0; i < groups.length; i++) {
    const group = groups[i];
    if (group.anchorInputMode.mode !== "table") {
      errors[i] = { anchors: "Нажмите «Обработать» для заполнения таблицы анкоров" };
      hasError = true;
      continue;
    }
    const anchors: RelevantAnchorEntry[] = group.anchorInputMode.rows.map((row) => ({
      text: row.text,
      articleCount: row.articleCount,
      uniqueness: row.uniqueness,
      generalTopic: row.generalTopic,
    }));
    tasks.push({
      targetUrl: group.targetUrl,
      anchors,
      ...(group.comment ? { comment: group.comment } : {}),
    });
  }
  if (hasError) {
    relevantTopicGroups = { tasks: null, errors };
  } else {
    relevantTopicGroups = { tasks, errors: null };
  }
  return relevantTopicGroups;
}

function sendCreateRequest(
  endpoint: string,
  body: object,
  setScreenState: (state: ScreenState) => void,
  navigate: ReturnType<typeof useNavigate>,
): void {
  setScreenState({ kind: "submitting" });

  fetchWithRefresh(endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  })
    .then(async (response) => {
      if (response.ok) {
        const outcome = (await response.json()) as CreateTasksResponse;
        setScreenState({ kind: "success" });
        if (outcome.tasks.length === 1) {
          void navigate(`/tasks/${outcome.tasks[0].taskId}/show`);
        } else {
          void navigate("/tasks");
        }
      } else {
        const errorBody = (await response.json()) as ErrorBody;
        const mapped = mapErrorFields(errorBody);
        setScreenState({ kind: "error", fieldErrors: mapped.fieldErrors, groupErrors: mapped.groupErrors });
      }
    })
    .catch(() => {
      setScreenState({ kind: "error", fieldErrors: {}, groupErrors: {} });
    });
}

function mapErrorFields(errorBody: ErrorBody): { fieldErrors: FieldErrors; groupErrors: GroupErrors } {
  let errorFields: { fieldErrors: FieldErrors; groupErrors: GroupErrors };
  const fieldErrors: FieldErrors = {};
  const groupErrors: GroupErrors = {};
  if (!Array.isArray(errorBody.fields)) {
    errorFields = { fieldErrors, groupErrors };
  } else {
    for (const field of errorBody.fields) {
      if (typeof field !== "string") {
        continue;
      }
      const match = field.match(/^tasks\[(\d+)\](?:\.(.+))?$/);
      if (match) {
        const index = Number(match[1]);
        const subpath = match[2] ?? "tasks";
        const key = subpathToKey(subpath);
        const message = GROUP_FIELD_ERROR_MESSAGES[key] ?? "Некорректное значение";
        const existing = groupErrors[index] ?? {};
        groupErrors[index] = { ...existing, [key]: message };
      } else {
        fieldErrors[field] = FIELD_ERROR_MESSAGES[field] ?? "Некорректное значение";
      }
    }
    errorFields = { fieldErrors, groupErrors };
  }
  return errorFields;
}

function subpathToKey(subpath: string): string {
  let subpathToKeyOutcome: string;
  if (subpath.startsWith("anchors")) {
    subpathToKeyOutcome = "anchors";
  } else {
    if (subpath.startsWith("topics")) {
      subpathToKeyOutcome = "topics";
    } else {
      if (subpath === "targetUrl") {
        subpathToKeyOutcome = "targetUrl";
      } else {
        if (subpath === "uniqueArticlesCount") {
          subpathToKeyOutcome = "uniqueArticlesCount";
        } else {
          subpathToKeyOutcome = subpath;
        }
      }
    }
  }
  return subpathToKeyOutcome;
}
